/**
 * Verification UI display helpers.
 *
 * Maps the Phase 0 VerificationDetailVM vocabulary (status enums, numeric
 * measurements) to UI-shaped values (chip variants, icons, formatted strings,
 * deviation-bar positions). Keeps components free of enum translation —
 * components consume Vuex getters that call these helpers.
 *
 * Reference: .claude/features/verification/phase0/phase0-endpoint-inventory.md
 * §2 — VerificationSummaryVM + CheckResultVM DTO shapes.
 */

// ---- Check status (Phase 0 CheckResultVM.Status) ----

export function toStatusChipVariant(status) {
  switch (status) {
    case 'Pass': return 'pass';
    case 'Fail': return 'fail';
    case 'Pending': return 'default';
    case 'UnableToVerify': return 'default';
    case 'NotApplicable': return 'default';
    default: return 'default';
  }
}

export function toStatusChipLabel(status, isActive) {
  if (isActive) return 'ACTIVE';
  switch (status) {
    case 'Pass': return 'PASS';
    case 'Fail': return 'FAIL';
    case 'Pending': return '—';
    case 'UnableToVerify': return 'UTV';
    case 'NotApplicable': return 'N/A';
    default: return '—';
  }
}

export function toChecklistState(status, isActive) {
  if (isActive) return 'active';
  switch (status) {
    case 'Pass': return 'done';
    case 'Fail': return 'fail';
    case 'Pending': return 'pending';
    case 'UnableToVerify': return 'pending';
    case 'NotApplicable': return 'done';
    default: return 'pending';
  }
}

/**
 * Variant key for the right-edge state tag in the left-panel checklist.
 * Drives the pill's color (pass = teal, fail = red, active = primary, else
 * muted grey).
 */
export function toChecklistTagVariant(status, isActive) {
  if (isActive) return 'active';
  switch (status) {
    case 'Pass': return 'pass';
    case 'Fail': return 'fail';
    default: return 'default';
  }
}

// ---- Overall result (Phase 0 VerificationSummaryVM.OverallResult) ----

export function toOverallShort(overallResult) {
  switch (overallResult) {
    case 'CONFORMANCE': return 'PASS';
    case 'NON-CONFORMANCE': return 'FAIL';
    case 'UNABLE_TO_DETERMINE': return '—';
    default: return '—';
  }
}

export function toOverallClassName(overallResult) {
  switch (overallResult) {
    case 'CONFORMANCE': return 'pass';
    case 'NON-CONFORMANCE': return 'fail';
    case 'UNABLE_TO_DETERMINE': return 'pending';
    default: return 'pending';
  }
}

// ---- Verification status (Phase 0 VerificationStatus enum) ----

export function toVerificationStatusChip(status) {
  const map = {
    Draft: { label: 'Draft', variant: 'default' },
    InProgress: { label: 'In Progress', variant: 'progress' },
    Complete: { label: 'Complete', variant: 'pass' },
    ReadyForReview: { label: 'Ready for Review', variant: 'primary' },
    Approved: { label: 'Approved', variant: 'pass' },
    Issued: { label: 'Issued', variant: 'primary' },
  };
  return map[status] || null;
}

// ---- Source readiness (Phase 0 SourceReadiness enum) ----

export function toSourceReadinessChip(readiness) {
  const map = {
    ScanOnly: { label: 'Source: Scan', variant: 'default' },
    ModelOnly: { label: 'Source: Model', variant: 'default' },
    ScanPlusModel: { label: 'Source: Scan + Model', variant: 'default' },
  };
  return map[readiness] || null;
}

// ---- Measurement formatters ----

function unitLabel(unit) {
  if (unit === 'mm_per_m') return 'mm/m';
  return unit || 'mm';
}

/**
 * Format a numeric measurement with unit.
 * @param {number|null} value
 * @param {string} unit - e.g. 'mm', 'degrees', 'mm_per_m'
 * @returns {string}
 */
export function formatMeasurement(value, unit) {
  if (value === null || value === undefined) return '—';
  return `${value} ${unitLabel(unit)}`;
}

/**
 * Format signed deviation with explicit sign (per Phase 0 decision Q17).
 * @param {number|null} deviation
 * @param {string} unit
 * @returns {string}
 */
export function formatDeviation(deviation, unit) {
  if (deviation === null || deviation === undefined) return '—';
  const sign = deviation > 0 ? '+' : '';
  return `${sign}${deviation} ${unitLabel(unit)}`;
}

/**
 * Format applied tolerance display (e.g. "±60 mm", "+25 mm", "≥ 5 mm/m").
 * @param {number} value
 * @param {string} unit
 * @param {string} symbol - '±' | '+' | '≥' | '≤'
 * @returns {string}
 */
export function formatTolerance(value, unit, symbol) {
  if (value === null || value === undefined) return '—';
  const sym = symbol || '±';
  return `${sym}${value} ${unitLabel(unit)}`;
}

/**
 * Compute deviation bar position percentage (0-100) for a check.
 * Bar range is symmetric: −2×tolerance to +2×tolerance.
 *
 * Returns null for missing / zero tolerance. Zero-tolerance rules would
 * produce a nonsensical `−0 / 0 / +0` bar; components must render a fallback
 * ("exact match required" text) when this returns null for a check that has
 * a measured value.
 *
 * @param {number|null} deviation
 * @param {number|null} toleranceValue
 * @returns {number|null} 0-100, or null if unmeasured / no finite tolerance
 */
export function computeDeviationPercent(deviation, toleranceValue) {
  if (deviation === null || deviation === undefined) return null;
  if (!toleranceValue) return null;
  const range = toleranceValue * 4; // -2t to +2t
  const normalized = ((deviation + 2 * toleranceValue) / range) * 100;
  return Math.max(0, Math.min(100, normalized));
}

/**
 * Deviation-bar tick labels like ['−60', '0', '+60'].
 * Returns null for missing / zero tolerance — see `computeDeviationPercent`
 * rationale; components must render a fallback when the bar is not drawable.
 *
 * @param {number|null} toleranceValue
 * @returns {string[]|null}
 */
export function toBarLabels(toleranceValue) {
  if (!toleranceValue) return null;
  return [`−${toleranceValue}`, '0', `+${toleranceValue}`];
}

